import { type ReactElement } from 'react';
import { Link } from 'react-router-dom';
import {
  FileText,
  Receipt,
  Building2,
  Users,
  Package,
  Calculator,
  BarChart3,
  FileSpreadsheet,
  PieChart,
  Send,
  Smartphone,
  Puzzle,
  ArrowRight,
} from 'lucide-react';

interface FeatureItem {
  icon: typeof FileText;
  title: string;
  description: string;
  href: string;
}

interface FeatureCategory {
  title: string;
  description: string;
  features: FeatureItem[];
}

const featureCategories: FeatureCategory[] = [
  {
    title: 'Buchhaltung',
    description: 'Alle Tools für Ihre tägliche Buchhaltung',
    features: [
      { icon: FileText, title: 'Rechnungen', description: 'Professionelle Rechnungen erstellen und versenden', href: '/funktionen/rechnungen' },
      { icon: Receipt, title: 'Aufträge & Angebote', description: 'Überzeugende Angebote erstellen und nachverfolgen', href: '/funktionen/auftraege' },
      { icon: Receipt, title: 'Belegerfassung', description: 'Belege per App oder E-Mail erfassen', href: '/funktionen/belegerfassung' },
      { icon: Building2, title: 'Banking', description: 'Bankkonten verbinden und automatisch verbuchen', href: '/funktionen/banking' },
      { icon: Users, title: 'Kontaktverwaltung', description: 'Kunden und Lieferanten verwalten', href: '/funktionen/kontakte' },
      { icon: Package, title: 'Produkte & Services', description: 'Artikelstamm und Dienstleistungen pflegen', href: '/funktionen/produkte' },
    ],
  },
  {
    title: 'Steuern & Auswertung',
    description: 'Steuern vorbereiten und Geschäftszahlen analysieren',
    features: [
      { icon: PieChart, title: 'Dashboard', description: 'Finanzübersicht und Kennzahlen', href: '/funktionen/uebersicht' },
      { icon: Calculator, title: 'Umsatzsteuer', description: 'UStVA erstellen und per ELSTER übermitteln', href: '/funktionen/umsatzsteuer' },
      { icon: FileSpreadsheet, title: 'EÜR & GuV', description: 'Einnahmen-Überschuss-Rechnung automatisch', href: '/funktionen/euer' },
      { icon: BarChart3, title: 'BWA', description: 'Betriebswirtschaftliche Auswertung', href: '/funktionen/bwa' },
      { icon: FileSpreadsheet, title: 'Summen- & Saldenliste', description: 'Alle Buchungen detailliert nachvollziehen', href: '/funktionen/saldenliste' },
      { icon: Send, title: 'Exporte', description: 'DATEV, ADDISON und weitere Formate', href: '/funktionen/exporte' },
    ],
  },
  {
    title: 'Erweiterungen',
    description: 'Mehr Möglichkeiten für Ihre Buchhaltung',
    features: [
      { icon: Puzzle, title: 'Integrationen', description: 'Verbindungen zu Shopsystemen und Tools', href: '/funktionen/integrationen' },
      { icon: Smartphone, title: 'Mobile App', description: 'Buchhaltung von unterwegs', href: '/funktionen/mobile-app' },
    ],
  },
];

export default function FunktionenUebersicht(): ReactElement {
  return (
    <div className="pt-20">
      {/* Hero */}
      <section className="py-16 lg:py-24 bg-gradient-to-br from-primary-50 via-white to-secondary-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl lg:text-5xl font-bold text-secondary-900 mb-6">
            Alle Funktionen im Überblick
          </h1>
          <p className="text-lg text-secondary-600 max-w-3xl mx-auto mb-8">
            Saldify bietet alle Werkzeuge, die Sie für eine professionelle Buchhaltung brauchen –
            von der Rechnungserstellung über die Belegerfassung bis zur Steuererklärung.
          </p>
          <Link
            to="/#preise"
            className="btn-primary px-8 py-4 text-base shadow-lg shadow-primary-600/30"
          >
            14 Tage kostenlos testen
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* Feature Categories */}
      {featureCategories.map((category, categoryIndex) => (
        <section
          key={category.title}
          className={`py-16 lg:py-24 ${categoryIndex % 2 === 0 ? 'bg-white' : 'bg-secondary-50'}`}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-secondary-900 mb-4">
                {category.title}
              </h2>
              <p className="text-lg text-secondary-600">
                {category.description}
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {category.features.map((feature) => (
                <Link
                  key={feature.title}
                  to={feature.href}
                  className="group bg-white rounded-xl p-6 border border-secondary-100 hover:border-primary-200 hover:shadow-xl transition-all duration-300"
                >
                  <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center mb-4 group-hover:bg-primary-200 transition-colors">
                    <feature.icon className="w-6 h-6 text-primary-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-secondary-900 mb-2 group-hover:text-primary-600 transition-colors">
                    {feature.title}
                  </h3>
                  <p className="text-secondary-600 text-sm mb-4">
                    {feature.description}
                  </p>
                  <span className="text-primary-600 text-sm font-medium inline-flex items-center gap-1 group-hover:gap-2 transition-all">
                    Mehr erfahren
                    <ArrowRight className="w-4 h-4" />
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>
      ))}

      {/* CTA */}
      <section className="py-16 lg:py-24 bg-gradient-to-r from-primary-600 to-primary-700">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
            Überzeugt?
          </h2>
          <p className="text-lg text-primary-100 mb-8">
            Testen Sie alle Funktionen 14 Tage kostenlos und unverbindlich.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/#preise"
              className="bg-white text-primary-600 hover:bg-primary-50 px-8 py-4 rounded-lg font-semibold transition-all shadow-lg inline-flex items-center justify-center gap-2"
            >
              Kostenlos starten
              <ArrowRight className="w-4 h-4" />
            </Link>
            <a
              href="#demo"
              className="border-2 border-white/30 text-white hover:bg-white/10 px-8 py-4 rounded-lg font-semibold transition-all inline-flex items-center justify-center"
            >
              Demo anfordern
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
